/**
 * SmartFlowsList - Grid de Smart Flows
 * Layout: Grid/List, responsive, light theme (AdminCorrections pattern)
 * Features: Search (nombre+WhatsApp), Ordenamiento, Selección múltiple,
 *           Eliminar, Duplicar, Toggle activo, Métricas, Stats, Animaciones,
 *           Renombrar inline, Favoritos, Exportar config, Filtros avanzados,
 *           Atajos de teclado, Badge de uso alto
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Checkbox,
  Fade,
  Grow,
  Tooltip,
  Switch,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  SmartToy as SmartToyIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as ConfigIcon,
  East as ArrowIcon,
  Sort as SortIcon,
  AccessTime as TimeIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  SelectAll as SelectAllIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  FileDownload as ExportIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { StyledDialog, inputStyles } from "../common";

const API_URL = process.env.REACT_APP_API_URL || "https://turismo.nara.chat/api";

// Gradientes por estado del chatbot
const GRADIENTS = {
  activeWhatsapp: "linear-gradient(135deg, #a855f7 0%, #6366f1 100%)",
  activeNoWhatsapp: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  inactive: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
};

const SHADOW_COLORS = {
  activeWhatsapp: "rgba(168, 85, 247, 0.4)",
  activeNoWhatsapp: "rgba(245, 158, 11, 0.4)",
  inactive: "rgba(100, 116, 139, 0.3)",
};

function getCardGradient(chatbot) {
  if (!chatbot.agent_enabled) return { gradient: GRADIENTS.inactive, shadow: SHADOW_COLORS.inactive };
  if (chatbot.whatsapp_number) return { gradient: GRADIENTS.activeWhatsapp, shadow: SHADOW_COLORS.activeWhatsapp };
  return { gradient: GRADIENTS.activeNoWhatsapp, shadow: SHADOW_COLORS.activeNoWhatsapp };
}

function timeAgo(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return "hace un momento";
  if (seconds < 3600) return `hace ${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `hace ${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `hace ${Math.floor(seconds / 86400)}d`;
  if (seconds < 2592000) return `hace ${Math.floor(seconds / 604800)}sem`;
  return date.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

function isRecentlyActive(dateString) {
  if (!dateString) return false;
  return (new Date() - new Date(dateString)) < 3600000;
}

function formatTokens(n) {
  if (!n) return "0";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return String(n);
}

// F8: Helper de nivel de uso
function getUsageLevel(used, limit) {
  if (!limit || !used) return null;
  const pct = (used / limit) * 100;
  if (pct >= 95) return "critical";
  if (pct >= 80) return "warning";
  return "normal";
}

const USAGE_COLORS = { warning: "#f59e0b", critical: "#ef4444" };

// Background pattern (light theme)
const bgPattern = `
  radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.06) 0%, transparent 50%),
  radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.04) 0%, transparent 40%),
  radial-gradient(circle at 50% 80%, rgba(245, 158, 11, 0.03) 0%, transparent 40%)
`;

export default function SmartFlowsList() {
  const navigate = useNavigate();
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("last_activity");
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [viewMode, setViewMode] = useState("grid");

  // Crear chatbot
  const [createDialog, setCreateDialog] = useState(false);
  const [newChatbotName, setNewChatbotName] = useState("");
  const [creating, setCreating] = useState(false);

  // Selección múltiple
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectionActive = selectedIds.size > 0;

  // Eliminar
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Duplicar
  const [duplicateDialog, setDuplicateDialog] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicating, setDuplicating] = useState(false);

  // F3: Renombrar inline
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  // F4: Favoritos (persistido en localStorage)
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem("smartflows_favorites") || "[]"); }
    catch { return []; }
  });

  // F6: Filtros avanzados
  const [advancedFilter, setAdvancedFilter] = useState(null);

  useEffect(() => { fetchChatbots(); }, []);

  // F4: Persistir favoritos
  useEffect(() => {
    localStorage.setItem("smartflows_favorites", JSON.stringify(favorites));
  }, [favorites]);

  const fetchChatbots = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/smart-flows/chatbots`);
      if (!response.ok) throw new Error("Error cargando chatbots");
      setChatbots(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChatbot = async () => {
    if (!newChatbotName.trim()) return;
    setCreating(true);
    try {
      const userId = localStorage.getItem("user_id");
      const params = new URLSearchParams({ name: newChatbotName.trim(), user_id: userId });
      const response = await fetch(`${API_URL}/smart-flows/chatbots?${params}`, { method: "POST" });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Error al crear chatbot"); }
      const data = await response.json();
      setCreateDialog(false);
      setNewChatbotName("");
      navigate(`/smart-flows/${data.chatbot.id}`);
    } catch (err) { setError(err.message); }
    finally { setCreating(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const idsToDelete = deleteTarget ? [deleteTarget.id] : Array.from(selectedIds);
      for (const id of idsToDelete) {
        const res = await fetch(`${API_URL}/flows/${id}`, { method: "DELETE" });
        if (!res.ok) { const err = await res.json(); throw new Error(err.detail || `Error eliminando chatbot ${id}`); }
      }
      setChatbots((prev) => prev.filter((b) => !idsToDelete.includes(b.id)));
      setSelectedIds(new Set());
      setDeleteDialog(false);
      setDeleteTarget(null);
    } catch (err) { setError(err.message); }
    finally { setDeleting(false); }
  };

  // Toggle activo/inactivo
  const handleToggleEnabled = useCallback(async (chatbotId) => {
    try {
      const res = await fetch(`${API_URL}/smart-flows/${chatbotId}/toggle-enabled`, { method: "PUT" });
      if (!res.ok) throw new Error("Error al cambiar estado");
      const data = await res.json();
      setChatbots((prev) => prev.map((b) => b.id === chatbotId ? { ...b, agent_enabled: data.enabled } : b));
    } catch (err) { setError(err.message); }
  }, []);

  // Duplicar
  const handleDuplicate = async () => {
    if (!duplicateTarget) return;
    setDuplicating(true);
    try {
      const res = await fetch(`${API_URL}/smart-flows/${duplicateTarget.id}/duplicate`, { method: "POST" });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Error al duplicar"); }
      setDuplicateDialog(false);
      setDuplicateTarget(null);
      await fetchChatbots();
    } catch (err) { setError(err.message); }
    finally { setDuplicating(false); }
  };

  // F3: Rename handlers
  const handleStartRename = useCallback((chatbot) => {
    setRenamingId(chatbot.id);
    setRenameValue(chatbot.name);
  }, []);

  const handleCancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameValue("");
  }, []);

  const handleSaveRename = useCallback(async (chatbotId) => {
    if (renamingId === null) return; // guard contra blur después de Enter
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed.length > 100) { handleCancelRename(); return; }
    setRenaming(true);
    try {
      const res = await fetch(`${API_URL}/smart-flows/${chatbotId}/rename`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      if (!res.ok) throw new Error("Error al renombrar");
      const data = await res.json();
      setChatbots((prev) => prev.map((b) => b.id === chatbotId ? { ...b, name: data.name } : b));
    } catch (err) { setError(err.message); }
    finally { setRenaming(false); handleCancelRename(); }
  }, [renameValue, renamingId, handleCancelRename]);

  // F4: Toggle favorito
  const toggleFavorite = useCallback((id) => {
    setFavorites((prev) => prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]);
  }, []);

  // F5: Exportar config
  const handleExport = useCallback(async (chatbot) => {
    try {
      const res = await fetch(`${API_URL}/smart-flows/${chatbot.id}`);
      if (!res.ok) throw new Error("Error obteniendo configuración");
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${chatbot.name.replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ\s-]/g, "").replace(/\s+/g, "_")}_config.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); }
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === filteredAndSortedChatbots.length) return new Set();
      return new Set(filteredAndSortedChatbots.map((b) => b.id));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbots, searchTerm, filter, sortBy, advancedFilter, favorites]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Filtrado y ordenamiento con F4 favoritos, F5 búsqueda WhatsApp, F6 filtros avanzados
  const filteredAndSortedChatbots = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let result = chatbots.filter((bot) => {
      const matchesSearch = bot.name.toLowerCase().includes(term)
        || (bot.whatsapp_number && bot.whatsapp_number.toLowerCase().includes(term));

      // Filtro primario
      let matchesPrimary = true;
      if (filter === "active") matchesPrimary = bot.agent_enabled;
      else if (filter === "inactive") matchesPrimary = !bot.agent_enabled;
      else if (filter === "favorites") matchesPrimary = favorites.includes(bot.id);

      // F6: Filtro avanzado (aditivo)
      let matchesAdvanced = true;
      if (advancedFilter === "whatsapp") matchesAdvanced = !!bot.whatsapp_number;
      else if (advancedFilter === "plan") matchesAdvanced = bot.plan_conversaciones != null;
      else if (advancedFilter === "no_config") matchesAdvanced = !bot.has_config;

      return matchesSearch && matchesPrimary && matchesAdvanced;
    });

    // Orden primario
    result.sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "last_activity") return (b.last_activity ? new Date(b.last_activity) : new Date(0)) - (a.last_activity ? new Date(a.last_activity) : new Date(0));
      if (sortBy === "created") return (b.created_at ? new Date(b.created_at) : new Date(0)) - (a.created_at ? new Date(a.created_at) : new Date(0));
      return 0;
    });

    // F4: Favoritos siempre primero (sort estable)
    result.sort((a, b) => {
      const aFav = favorites.includes(a.id) ? 1 : 0;
      const bFav = favorites.includes(b.id) ? 1 : 0;
      return bFav - aFav;
    });

    return result;
  }, [chatbots, searchTerm, filter, sortBy, favorites, advancedFilter]);

  // Stats con métricas agregadas
  const stats = useMemo(() => ({
    total: chatbots.length,
    active: chatbots.filter((b) => b.agent_enabled).length,
    inactive: chatbots.filter((b) => !b.agent_enabled).length,
    totalConversaciones: chatbots.reduce((s, b) => s + (b.conversaciones_totales || 0), 0),
    totalSesiones: chatbots.reduce((s, b) => s + (b.sessions_count || 0), 0),
    recentlyActive: chatbots.filter((b) => isRecentlyActive(b.last_activity)).length,
  }), [chatbots]);

  const sortOptions = [
    { value: "last_activity", label: "Última actividad" },
    { value: "name", label: "Nombre" },
    { value: "created", label: "Fecha de creación" },
  ];

  // Shared card action props
  const cardActions = useCallback((chatbot) => ({
    selected: selectedIds.has(chatbot.id),
    selectionActive,
    onSelect: () => toggleSelect(chatbot.id),
    onDelete: () => { setDeleteTarget({ id: chatbot.id, name: chatbot.name }); setDeleteDialog(true); },
    onDuplicate: () => { setDuplicateTarget({ id: chatbot.id, name: chatbot.name }); setDuplicateDialog(true); },
    onToggle: () => handleToggleEnabled(chatbot.id),
    onClick: () => navigate(`/smart-flows/${chatbot.id}`),
    // F3
    renamingId, renameValue, renaming,
    onRenameChange: setRenameValue,
    onStartRename: () => handleStartRename(chatbot),
    onSaveRename: () => handleSaveRename(chatbot.id),
    onCancelRename: handleCancelRename,
    // F4
    isFavorite: favorites.includes(chatbot.id),
    onToggleFavorite: () => toggleFavorite(chatbot.id),
    // F5
    onExport: () => handleExport(chatbot),
  }), [selectedIds, selectionActive, toggleSelect, handleToggleEnabled, navigate,
    renamingId, renameValue, renaming, handleStartRename, handleSaveRename, handleCancelRename,
    favorites, toggleFavorite, handleExport]);

  // F7: Atajos de teclado
  useEffect(() => {
    const handler = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const mod = isMac ? e.metaKey : e.ctrlKey;
      const tag = e.target.tagName;
      const isInput = tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable;

      if (e.key === "Escape") {
        if (renamingId) { handleCancelRename(); return; }
        if (createDialog || deleteDialog || duplicateDialog) return;
        if (selectionActive) { clearSelection(); e.preventDefault(); return; }
      }

      if (isInput) return;

      if (mod && e.key === "n") {
        e.preventDefault();
        setCreateDialog(true);
      }

      if ((e.key === "Delete" || e.key === "Backspace") && selectionActive) {
        e.preventDefault();
        setDeleteTarget(null);
        setDeleteDialog(true);
      }

      if (mod && e.key === "a") {
        e.preventDefault();
        toggleSelectAll();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [renamingId, handleCancelRename, createDialog, deleteDialog, duplicateDialog, selectionActive, clearSelection, toggleSelectAll]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f1f5f9", backgroundImage: bgPattern }}>
        <CircularProgress sx={{ color: "#a855f7" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f1f5f9",
        backgroundImage: bgPattern,
        p: { xs: 2, md: 3 },
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => navigate("/dashboard")} sx={{ color: "#64748b", p: 0.5, "&:hover": { color: "#1e293b", bgcolor: "#e2e8f0" } }}>
            <BackIcon fontSize="small" />
          </IconButton>
          <Box sx={{ width: 34, height: 34, borderRadius: 2, background: "linear-gradient(135deg, #a855f7 0%, #6366f1 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(168, 85, 247, 0.4)" }}>
            <SmartToyIcon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
          <Typography sx={{ color: "#1e293b", fontWeight: 700, fontSize: 17 }}>Smart Flows</Typography>
          <Box sx={{ px: 1, py: 0.25, borderRadius: 1, background: "linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)", boxShadow: "0 2px 8px rgba(236, 72, 153, 0.4)" }}>
            <Typography sx={{ color: "#fff", fontSize: 9, fontWeight: 700 }}>Beta</Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreateDialog(true)}
            sx={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", fontSize: 12, fontWeight: 600, textTransform: "none", borderRadius: 2, px: 2, py: 0.75, boxShadow: "0 4px 14px rgba(16, 185, 129, 0.4)", "&:hover": { background: "linear-gradient(135deg, #059669 0%, #047857 100%)", boxShadow: "0 6px 20px rgba(16, 185, 129, 0.5)" } }}>
            Nuevo
          </Button>

          <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small"
            sx={{ "& .MuiToggleButton-root": { color: "#94a3b8", border: "1px solid #e2e8f0", p: 0.5, "&.Mui-selected": { color: "#a855f7", bgcolor: "rgba(168,85,247,0.08)", borderColor: "rgba(168,85,247,0.3)" }, "&:hover": { bgcolor: "rgba(0,0,0,0.02)" } } }}>
            <ToggleButton value="grid"><ViewModuleIcon sx={{ fontSize: 18 }} /></ToggleButton>
            <ToggleButton value="list"><ViewListIcon sx={{ fontSize: 18 }} /></ToggleButton>
          </ToggleButtonGroup>

          <IconButton onClick={(e) => setSortMenuAnchor(e.currentTarget)} sx={{ color: "#64748b", p: 0.75, "&:hover": { color: "#1e293b", bgcolor: "#e2e8f0" } }}>
            <SortIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={sortMenuAnchor} open={Boolean(sortMenuAnchor)} onClose={() => setSortMenuAnchor(null)}
            PaperProps={{ sx: { bgcolor: "rgba(255,255,255,0.95)", backdropFilter: "blur(20px)", border: "1px solid #e2e8f0", borderRadius: 2, minWidth: 180, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" } }}>
            {sortOptions.map((o) => (
              <MenuItem key={o.value} selected={sortBy === o.value} onClick={() => { setSortBy(o.value); setSortMenuAnchor(null); }}
                sx={{ fontSize: 13, color: sortBy === o.value ? "#a855f7" : "#475569", "&:hover": { bgcolor: "rgba(168,85,247,0.06)" }, "&.Mui-selected": { bgcolor: "rgba(168,85,247,0.08)" } }}>
                {o.label}
              </MenuItem>
            ))}
          </Menu>

          <TextField placeholder="Nombre o #..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small"
            sx={{ width: 170, "& .MuiOutlinedInput-root": { height: 34, bgcolor: "#fff", color: "#1e293b", fontSize: 12, borderRadius: 2, "& fieldset": { borderColor: "#e2e8f0" }, "&:hover fieldset": { borderColor: "#cbd5e1" }, "&.Mui-focused fieldset": { borderColor: "#a855f7" } } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8", fontSize: 16 }} /></InputAdornment> }}
          />
        </Box>
      </Box>

      {/* Stats bar */}
      {chatbots.length > 0 && (
        <Box sx={{ display: "flex", gap: 2.5, mb: 2, flexWrap: "wrap" }}>
          {[
            { label: "activos", value: stats.active, color: "#10b981" },
            { label: "conversaciones", value: stats.totalConversaciones, color: "#a855f7" },
            { label: "sesiones", value: stats.totalSesiones, color: "#6366f1" },
            ...(stats.recentlyActive > 0 ? [{ label: "activos ahora", value: stats.recentlyActive, color: "#f59e0b" }] : []),
          ].map((s) => (
            <Box key={s.label} sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, background: `linear-gradient(135deg, ${s.color}, ${s.color}cc)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                {s.value}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, py: 0.5, borderRadius: 2 }}>{error}</Alert>}

      {/* Toolbar de selección */}
      <Fade in={selectionActive}>
        <Box sx={{ display: selectionActive ? "flex" : "none", alignItems: "center", gap: 2, mb: 2, px: 2.5, py: 1.5, borderRadius: 3, background: "rgba(255,255,255,0.9)", backdropFilter: "blur(20px)", border: "1px solid rgba(168,85,247,0.2)", boxShadow: "0 4px 16px rgba(168,85,247,0.1)" }}>
          <Typography sx={{ color: "#7c3aed", fontSize: 13, fontWeight: 600 }}>{selectedIds.size} seleccionado{selectedIds.size > 1 ? "s" : ""}</Typography>
          <Button size="small" startIcon={<SelectAllIcon sx={{ fontSize: 14 }} />} onClick={toggleSelectAll}
            sx={{ color: "#7c3aed", fontSize: 11, textTransform: "none", "&:hover": { bgcolor: "rgba(168,85,247,0.06)" } }}>
            {selectedIds.size === filteredAndSortedChatbots.length ? "Deseleccionar" : "Seleccionar todos"}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<DeleteIcon sx={{ fontSize: 14 }} />} onClick={() => { setDeleteTarget(null); setDeleteDialog(true); }}
            sx={{ color: "#dc2626", fontSize: 11, fontWeight: 600, textTransform: "none", bgcolor: "rgba(239,68,68,0.08)", borderRadius: 2, px: 1.5, "&:hover": { bgcolor: "rgba(239,68,68,0.15)" } }}>
            Eliminar ({selectedIds.size})
          </Button>
          <IconButton size="small" onClick={clearSelection} sx={{ color: "#94a3b8", p: 0.5, "&:hover": { color: "#64748b" } }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>
      </Fade>

      {/* Filtros primarios */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
        <FilterChip label={`Todos ${stats.total}`} active={filter === "all"} onClick={() => setFilter("all")} />
        <FilterChip label={`Activos ${stats.active}`} active={filter === "active"} onClick={() => setFilter("active")} />
        <FilterChip label={`Inactivos ${stats.inactive}`} active={filter === "inactive"} onClick={() => setFilter("inactive")} />
        {favorites.length > 0 && (
          <FilterChip label={`Favoritos ${favorites.length}`} active={filter === "favorites"} onClick={() => setFilter("favorites")} />
        )}
      </Box>

      {/* F6: Filtros avanzados */}
      <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
        {[
          { key: "whatsapp", label: "Con WhatsApp" },
          { key: "plan", label: "Con Plan" },
          { key: "no_config", label: "Sin configurar" },
        ].map((f) => (
          <FilterChip key={f.key} label={f.label} active={advancedFilter === f.key}
            onClick={() => setAdvancedFilter((prev) => prev === f.key ? null : f.key)} />
        ))}
      </Box>

      {/* Content */}
      {filteredAndSortedChatbots.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(20px)", borderRadius: 4, border: "1px solid rgba(255,255,255,0.5)" }}>
          <SmartToyIcon sx={{ fontSize: 56, color: "#cbd5e1", mb: 1.5 }} />
          <Typography sx={{ color: "#1e293b", fontSize: 16, fontWeight: 600, mb: 0.5 }}>{searchTerm ? "Sin resultados" : "No hay chatbots"}</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13, mb: 2 }}>{searchTerm ? "Intenta con otro término de búsqueda" : "Crea tu primer chatbot inteligente para comenzar"}</Typography>
          {!searchTerm && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialog(true)}
              sx={{ background: "linear-gradient(135deg, #a855f7 0%, #6366f1 100%)", textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3, boxShadow: "0 4px 14px rgba(168,85,247,0.4)", "&:hover": { boxShadow: "0 6px 20px rgba(168,85,247,0.5)" } }}>
              Crear Chatbot
            </Button>
          )}
        </Box>
      ) : viewMode === "list" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {filteredAndSortedChatbots.map((chatbot, index) => (
            <Fade key={chatbot.id} in={true} timeout={Math.min(200 + index * 40, 800)}>
              <Box><ListRow chatbot={chatbot} {...cardActions(chatbot)} /></Box>
            </Fade>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 240px))", gap: 2 }}>
          {filteredAndSortedChatbots.map((chatbot, index) => (
            <Grow key={chatbot.id} in={true} style={{ transformOrigin: "0 0 0" }} timeout={Math.min(300 + index * 60, 1200)}>
              <Box><FlowCard chatbot={chatbot} {...cardActions(chatbot)} /></Box>
            </Grow>
          ))}
        </Box>
      )}

      {/* F7: Hint de atajos */}
      <Tooltip title="Ctrl+N: Nuevo | Ctrl+A: Seleccionar | Del: Eliminar | Esc: Cancelar" arrow placement="top-end">
        <Box sx={{
          position: "fixed", bottom: 16, right: 16,
          px: 1.5, py: 0.5, borderRadius: 2,
          bgcolor: "rgba(255,255,255,0.85)", backdropFilter: "blur(10px)",
          border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          cursor: "default", opacity: 0.5,
          transition: "opacity 0.2s", "&:hover": { opacity: 1 },
        }}>
          <Typography sx={{ color: "#94a3b8", fontSize: 10 }}>
            Atajos
          </Typography>
        </Box>
      </Tooltip>

      {/* Dialog crear chatbot */}
      <StyledDialog open={createDialog} onClose={() => !creating && setCreateDialog(false)} title="Nuevo Chatbot Inteligente" icon={<SmartToyIcon />} variant="compact" maxWidth="xs" disableClose={creating}>
        <StyledDialog.Body compact>
          <Typography sx={{ color: "#64748b", fontSize: 12 }}>El chatbot se creará con el agente IA activado y todas las herramientas disponibles.</Typography>
          <TextField autoFocus fullWidth label="Nombre del chatbot" value={newChatbotName} onChange={(e) => setNewChatbotName(e.target.value)} placeholder="Ej: Asistente de Turismo" size="small" sx={inputStyles}
            onKeyDown={(e) => { if (e.key === "Enter" && newChatbotName.trim()) handleCreateChatbot(); }} />
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setCreateDialog(false)} disabled={creating} />
          <StyledDialog.PrimaryButton fullWidth onClick={handleCreateChatbot} disabled={!newChatbotName.trim()} loading={creating}>Crear Chatbot</StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* Dialog eliminar */}
      <StyledDialog open={deleteDialog} onClose={() => !deleting && setDeleteDialog(false)} title={deleteTarget ? "Eliminar Chatbot" : `Eliminar ${selectedIds.size} chatbot${selectedIds.size > 1 ? "s" : ""}`} icon={<DeleteIcon />} variant="compact" maxWidth="xs" disableClose={deleting}>
        <StyledDialog.Body compact>
          <Box sx={{ p: 2, bgcolor: "#fef2f2", borderRadius: 2, border: "1px solid #fecaca" }}>
            <Typography sx={{ fontSize: 13, color: "#991b1b", fontWeight: 600, mb: 0.5 }}>Esta acción no se puede deshacer</Typography>
            <Typography sx={{ fontSize: 12, color: "#dc2626" }}>
              {deleteTarget
                ? `Se eliminará "${deleteTarget.name}" y toda su configuración, base de conocimiento y sesiones.`
                : `Se eliminarán ${selectedIds.size} chatbot${selectedIds.size > 1 ? "s" : ""} y toda su configuración.`}
            </Typography>
          </Box>
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => { setDeleteDialog(false); setDeleteTarget(null); }} disabled={deleting} />
          <Button fullWidth variant="contained" onClick={handleDelete} disabled={deleting}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, bgcolor: "#dc2626", py: 1, boxShadow: "0 4px 14px rgba(220,38,38,0.4)", "&:hover": { bgcolor: "#b91c1c", boxShadow: "0 6px 20px rgba(220,38,38,0.5)" } }}>
            {deleting ? <CircularProgress size={20} color="inherit" /> : "Eliminar"}
          </Button>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* Dialog duplicar */}
      <StyledDialog open={duplicateDialog} onClose={() => !duplicating && setDuplicateDialog(false)} title="Duplicar Chatbot" icon={<CopyIcon />} variant="compact" maxWidth="xs" disableClose={duplicating}>
        <StyledDialog.Body compact>
          <Typography sx={{ fontSize: 13, color: "#475569" }}>
            Se creará una copia de <strong>"{duplicateTarget?.name}"</strong> con toda su configuración y base de conocimiento.
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>La copia se creará desactivada y sin número de WhatsApp.</Typography>
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => { setDuplicateDialog(false); setDuplicateTarget(null); }} disabled={duplicating} />
          <StyledDialog.PrimaryButton fullWidth onClick={handleDuplicate} loading={duplicating}>Duplicar</StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>
    </Box>
  );
}

// Chip de filtro (light theme)
function FilterChip({ label, active, onClick }) {
  return (
    <Box onClick={onClick}
      sx={{ px: 1.5, py: 0.5, borderRadius: 2, cursor: "pointer", fontSize: 12, fontWeight: 600,
        color: active ? "#7c3aed" : "#64748b",
        bgcolor: active ? "rgba(168,85,247,0.08)" : "rgba(255,255,255,0.7)",
        border: active ? "1px solid rgba(168,85,247,0.3)" : "1px solid #e2e8f0",
        backdropFilter: "blur(10px)", transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
        "&:hover": { bgcolor: active ? "rgba(168,85,247,0.12)" : "rgba(255,255,255,0.9)", color: active ? "#7c3aed" : "#475569" } }}>
      {label}
    </Box>
  );
}

// ============ GRID CARD ============
function FlowCard({ chatbot, selected, selectionActive, onSelect, onDelete, onDuplicate, onToggle, onClick,
  renamingId, renameValue, renaming, onRenameChange, onStartRename, onSaveRename, onCancelRename,
  isFavorite, onToggleFavorite, onExport }) {
  const [hovered, setHovered] = useState(false);
  const lastActivity = timeAgo(chatbot.last_activity);
  const { gradient, shadow } = getCardGradient(chatbot);
  const showCheckbox = selectionActive || hovered;
  const recentActive = isRecentlyActive(chatbot.last_activity);

  // F8: Nivel de uso
  const convLevel = getUsageLevel(chatbot.conversaciones_totales, chatbot.plan_conversaciones);
  const tokenLevel = getUsageLevel(chatbot.tokens_usados, chatbot.plan_tokens);
  const usageLevel = convLevel === "critical" || tokenLevel === "critical" ? "critical"
    : convLevel === "warning" || tokenLevel === "warning" ? "warning" : null;

  return (
    <Box onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      sx={{
        position: "relative", borderRadius: 3, background: "rgba(255,255,255,0.85)", backdropFilter: "blur(20px)",
        cursor: "pointer", transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)", overflow: "hidden",
        display: "flex", flexDirection: "column", minHeight: 130,
        boxShadow: selected ? `0 0 0 3px ${shadow}, 0 4px 24px rgba(0,0,0,0.08)` : "0 4px 24px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
        border: selected ? `2px solid ${shadow.replace("0.4","0.8")}` : "1px solid rgba(255,255,255,0.5)",
        "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: 3, background: gradient },
        "&:hover": { transform: "translateY(-4px) scale(1.02)", boxShadow: `0 20px 40px rgba(0,0,0,0.12), 0 8px 16px ${shadow}` },
        "&:active": { transform: "translateY(-2px) scale(0.98)" },
      }}>
      {/* Top actions */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 1, px: 1.25, minHeight: 26 }}>
        <Fade in={showCheckbox}>
          <Checkbox size="small" checked={selected} onClick={(e) => { e.stopPropagation(); onSelect(); }}
            sx={{ p: 0.25, color: "#94a3b8", "&.Mui-checked": { color: "#a855f7" }, opacity: showCheckbox ? 1 : 0 }} />
        </Fade>
        <Box sx={{ display: "flex", gap: 0.25 }}>
          {/* F4: Favorito */}
          <Tooltip title={isFavorite ? "Quitar favorito" : "Favorito"} arrow>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
              sx={{ p: 0.5, color: isFavorite ? "#f59e0b" : "#94a3b8",
                opacity: isFavorite ? 1 : (hovered ? 1 : 0), transition: "all 0.2s",
                "&:hover": { color: "#f59e0b", bgcolor: "rgba(245,158,11,0.1)" } }}>
              {isFavorite ? <StarIcon sx={{ fontSize: 14 }} /> : <StarBorderIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Tooltip>
          {/* F5: Exportar */}
          <Fade in={hovered}>
            <Tooltip title="Exportar config" arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onExport(); }}
                sx={{ p: 0.5, color: "#94a3b8", opacity: hovered ? 1 : 0, "&:hover": { color: "#0ea5e9", bgcolor: "rgba(14,165,233,0.1)" } }}>
                <ExportIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Fade>
          <Fade in={hovered}>
            <Tooltip title="Duplicar" arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                sx={{ p: 0.5, color: "#94a3b8", opacity: hovered ? 1 : 0, "&:hover": { color: "#6366f1", bgcolor: "rgba(99,102,241,0.1)" } }}>
                <CopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Fade>
          <Fade in={hovered}>
            <Tooltip title="Eliminar" arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }}
                sx={{ p: 0.5, color: "#94a3b8", opacity: hovered ? 1 : 0, "&:hover": { color: "#ef4444", bgcolor: "rgba(239,68,68,0.1)" } }}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Fade>
        </Box>
      </Box>

      {/* Main */}
      <Box onClick={onClick} sx={{ flex: 1, display: "flex", flexDirection: "column", px: 1.5, pb: 1.5 }}>
        {/* Icon + Name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.75 }}>
          <Box sx={{ position: "relative", width: 36, height: 36, borderRadius: 2.5, background: gradient, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", boxShadow: `0 6px 16px ${shadow}`, flexShrink: 0 }}>
            <SmartToyIcon sx={{ fontSize: 18 }} />
            {/* Pulse indicator */}
            {recentActive && (
              <Box sx={{
                position: "absolute", top: -2, right: -2, width: 8, height: 8, borderRadius: "50%",
                bgcolor: "#10b981", border: "2px solid rgba(255,255,255,0.9)",
                "@keyframes pulse": { "0%": { boxShadow: "0 0 0 0 rgba(16,185,129,0.6)" }, "70%": { boxShadow: "0 0 0 5px rgba(16,185,129,0)" }, "100%": { boxShadow: "0 0 0 0 rgba(16,185,129,0)" } },
                animation: "pulse 2s ease-in-out infinite",
              }} />
            )}
            {/* F8: Badge de uso alto */}
            {usageLevel && (
              <Tooltip title={(() => {
                const parts = [];
                const pctConv = chatbot.plan_conversaciones ? Math.round((chatbot.conversaciones_totales / chatbot.plan_conversaciones) * 100) : null;
                const pctTok = chatbot.plan_tokens ? Math.round((chatbot.tokens_usados / chatbot.plan_tokens) * 100) : null;
                if (pctConv && pctConv >= 80) parts.push(`${pctConv}% conversaciones`);
                if (pctTok && pctTok >= 80) parts.push(`${pctTok}% tokens`);
                return parts.join(" | ");
              })()} arrow>
                <Box sx={{
                  position: "absolute", bottom: -1, left: -1, width: 8, height: 8, borderRadius: "50%",
                  bgcolor: USAGE_COLORS[usageLevel], border: "2px solid rgba(255,255,255,0.9)",
                  boxShadow: `0 0 6px ${USAGE_COLORS[usageLevel]}`,
                }} />
              </Tooltip>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* F3: Rename inline */}
            {renamingId === chatbot.id ? (
              <TextField
                autoFocus size="small" value={renameValue}
                onChange={(e) => onRenameChange(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") onSaveRename();
                  if (e.key === "Escape") onCancelRename();
                }}
                onBlur={onSaveRename}
                onClick={(e) => e.stopPropagation()}
                disabled={renaming}
                inputProps={{ maxLength: 100, style: { fontSize: 13, fontWeight: 700, padding: "2px 6px" } }}
                sx={{ "& .MuiOutlinedInput-root": { height: 26, bgcolor: "#fff", borderRadius: 1, "& fieldset": { borderColor: "#a855f7" }, "&.Mui-focused fieldset": { borderColor: "#a855f7" } } }}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  sx={{ color: "#1e293b", fontWeight: 700, fontSize: 13, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {chatbot.name}
                </Typography>
                <Tooltip title="Renombrar" arrow>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onStartRename(); }}
                    sx={{ p: 0.25, color: "#94a3b8", opacity: hovered ? 1 : 0, transition: "opacity 0.2s", "&:hover": { color: "#a855f7", bgcolor: "rgba(168,85,247,0.1)" } }}>
                    <EditIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {chatbot.whatsapp_number && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                <WhatsAppIcon sx={{ fontSize: 11, color: "#25D366" }} />
                <Typography sx={{ color: "#64748b", fontSize: 10 }}>{chatbot.whatsapp_number}</Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Metadata */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, flexWrap: "wrap" }}>
          {lastActivity && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <TimeIcon sx={{ fontSize: 10, color: "#94a3b8" }} />
              <Typography sx={{ color: "#64748b", fontSize: 10 }}>{lastActivity}</Typography>
            </Box>
          )}
          {lastActivity && chatbot.sessions_count > 0 && <Typography sx={{ color: "#cbd5e1", fontSize: 9 }}>·</Typography>}
          {chatbot.sessions_count > 0 && <Typography sx={{ color: "#64748b", fontSize: 10 }}>{chatbot.sessions_count} sesiones</Typography>}
          {chatbot.kb_entries_count > 0 && (
            <><Typography sx={{ color: "#cbd5e1", fontSize: 9 }}>·</Typography><Typography sx={{ color: "#64748b", fontSize: 10 }}>{chatbot.kb_entries_count} KB</Typography></>
          )}
        </Box>

        {/* Mini metrics con F8: colores dinámicos */}
        {(chatbot.conversaciones_totales > 0 || chatbot.tokens_usados > 0) && (
          <Box sx={{ mb: 0.5 }}>
            {chatbot.conversaciones_totales > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
                <Typography sx={{ fontSize: 9, color: "#94a3b8", minWidth: 18 }}>{chatbot.conversaciones_totales}</Typography>
                <LinearProgress variant="determinate"
                  value={chatbot.plan_conversaciones ? Math.min((chatbot.conversaciones_totales / chatbot.plan_conversaciones) * 100, 100) : 50}
                  sx={{ flex: 1, height: 3, borderRadius: 2, bgcolor: "rgba(0,0,0,0.06)", "& .MuiLinearProgress-bar": {
                    bgcolor: convLevel === "critical" ? "#ef4444" : convLevel === "warning" ? "#f59e0b" : "#10b981", borderRadius: 2 } }} />
              </Box>
            )}
            {chatbot.tokens_usados > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ fontSize: 9, color: "#94a3b8", minWidth: 18 }}>{formatTokens(chatbot.tokens_usados)}</Typography>
                <LinearProgress variant="determinate"
                  value={chatbot.plan_tokens ? Math.min((chatbot.tokens_usados / chatbot.plan_tokens) * 100, 100) : 30}
                  sx={{ flex: 1, height: 3, borderRadius: 2, bgcolor: "rgba(0,0,0,0.06)", "& .MuiLinearProgress-bar": {
                    bgcolor: tokenLevel === "critical" ? "#ef4444" : tokenLevel === "warning" ? "#f59e0b" : "#6366f1", borderRadius: 2 } }} />
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Footer */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
            <Tooltip title={!chatbot.has_config ? "Configura primero el agente" : chatbot.agent_enabled ? "Desactivar" : "Activar"} arrow>
              <span>
                <Switch size="small" checked={chatbot.agent_enabled} disabled={!chatbot.has_config}
                  onClick={(e) => { e.stopPropagation(); onToggle(); }}
                  sx={{ width: 36, height: 20, p: 0,
                    "& .MuiSwitch-switchBase": { p: "2px", "&.Mui-checked": { transform: "translateX(16px)", color: "#fff", "& + .MuiSwitch-track": { bgcolor: "#10b981", opacity: 1 } }, "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.3 } },
                    "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.2)" },
                    "& .MuiSwitch-track": { borderRadius: 10, bgcolor: "#cbd5e1", opacity: 1 },
                  }} />
              </span>
            </Tooltip>
            {chatbot.has_config ? (
              <Chip icon={<ConfigIcon sx={{ fontSize: 9 }} />} label="Config" size="small"
                sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: "rgba(99,102,241,0.1)", color: "#6366f1", border: "1px solid rgba(99,102,241,0.2)", "& .MuiChip-icon": { color: "#6366f1", ml: 0.5 }, "& .MuiChip-label": { px: 0.5 } }} />
            ) : (
              <Chip label="Pendiente" size="small"
                sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: "rgba(245,158,11,0.1)", color: "#d97706", border: "1px solid rgba(245,158,11,0.2)", "& .MuiChip-label": { px: 0.75 } }} />
            )}
          </Box>
          <ArrowIcon sx={{ fontSize: 14, color: hovered ? "#a855f7" : "#cbd5e1", transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)", transform: hovered ? "translateX(3px)" : "none" }} />
        </Box>
      </Box>
    </Box>
  );
}

// ============ LIST ROW ============
function ListRow({ chatbot, selected, selectionActive, onSelect, onDelete, onDuplicate, onToggle, onClick,
  renamingId, renameValue, renaming, onRenameChange, onStartRename, onSaveRename, onCancelRename,
  isFavorite, onToggleFavorite, onExport }) {
  const [hovered, setHovered] = useState(false);
  const { gradient, shadow } = getCardGradient(chatbot);
  const lastActivity = timeAgo(chatbot.last_activity);
  const recentActive = isRecentlyActive(chatbot.last_activity);
  const showCheckbox = selectionActive || hovered;

  // F8
  const convLevel = getUsageLevel(chatbot.conversaciones_totales, chatbot.plan_conversaciones);
  const tokenLevel = getUsageLevel(chatbot.tokens_usados, chatbot.plan_tokens);
  const usageLevel = convLevel === "critical" || tokenLevel === "critical" ? "critical"
    : convLevel === "warning" || tokenLevel === "warning" ? "warning" : null;

  return (
    <Box onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1, position: "relative", overflow: "hidden",
        borderRadius: 2.5, background: "rgba(255,255,255,0.85)", backdropFilter: "blur(20px)", cursor: "pointer",
        border: selected ? `2px solid ${shadow}` : "1px solid rgba(255,255,255,0.5)",
        boxShadow: selected ? `0 0 0 2px ${shadow}` : "0 2px 12px rgba(0,0,0,0.06)",
        transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
        "&:hover": { boxShadow: `0 4px 16px ${shadow}`, transform: "translateX(2px)" },
        "&::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: gradient },
      }}>
      {/* Checkbox */}
      <Fade in={showCheckbox}>
        <Checkbox size="small" checked={selected} onClick={(e) => { e.stopPropagation(); onSelect(); }}
          sx={{ p: 0.25, color: "#94a3b8", "&.Mui-checked": { color: "#a855f7" }, opacity: showCheckbox ? 1 : 0, minWidth: 28 }} />
      </Fade>

      {/* Icon */}
      <Box sx={{ position: "relative", width: 28, height: 28, borderRadius: 1.5, background: gradient, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0, boxShadow: `0 4px 10px ${shadow}` }}>
        <SmartToyIcon sx={{ fontSize: 14 }} />
        {recentActive && (
          <Box sx={{ position: "absolute", top: -1, right: -1, width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981", border: "1.5px solid rgba(255,255,255,0.9)",
            "@keyframes pulse": { "0%": { boxShadow: "0 0 0 0 rgba(16,185,129,0.6)" }, "70%": { boxShadow: "0 0 0 4px rgba(16,185,129,0)" }, "100%": { boxShadow: "0 0 0 0 rgba(16,185,129,0)" } },
            animation: "pulse 2s ease-in-out infinite" }} />
        )}
      </Box>

      {/* Name + WhatsApp */}
      <Box onClick={onClick} sx={{ flex: 1, minWidth: 0 }}>
        {renamingId === chatbot.id ? (
          <TextField
            autoFocus size="small" value={renameValue}
            onChange={(e) => onRenameChange(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") onSaveRename();
              if (e.key === "Escape") onCancelRename();
            }}
            onBlur={onSaveRename}
            onClick={(e) => e.stopPropagation()}
            disabled={renaming}
            inputProps={{ maxLength: 100, style: { fontSize: 13, fontWeight: 700, padding: "2px 6px" } }}
            sx={{ "& .MuiOutlinedInput-root": { height: 26, bgcolor: "#fff", borderRadius: 1, "& fieldset": { borderColor: "#a855f7" }, "&.Mui-focused fieldset": { borderColor: "#a855f7" } } }}
          />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              sx={{ color: "#1e293b", fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {chatbot.name}
            </Typography>
            <Tooltip title="Renombrar" arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onStartRename(); }}
                sx={{ p: 0.25, color: "#94a3b8", opacity: hovered ? 1 : 0, transition: "opacity 0.2s", "&:hover": { color: "#a855f7", bgcolor: "rgba(168,85,247,0.1)" } }}>
                <EditIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {chatbot.whatsapp_number && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <WhatsAppIcon sx={{ fontSize: 10, color: "#25D366" }} />
            <Typography sx={{ color: "#64748b", fontSize: 10 }}>{chatbot.whatsapp_number}</Typography>
          </Box>
        )}
      </Box>

      {/* Metadata */}
      <Box onClick={onClick} sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1, minWidth: 140 }}>
        {lastActivity && <Typography sx={{ color: "#64748b", fontSize: 10 }}>{lastActivity}</Typography>}
        {chatbot.sessions_count > 0 && <Typography sx={{ color: "#94a3b8", fontSize: 10 }}>{chatbot.sessions_count} ses.</Typography>}
        {chatbot.conversaciones_totales > 0 && <Typography sx={{ color: "#94a3b8", fontSize: 10 }}>{chatbot.conversaciones_totales} conv.</Typography>}
        {/* F8: Warning icon */}
        {usageLevel && (
          <Tooltip title={usageLevel === "critical" ? "Uso crítico (>95%)" : "Uso alto (>80%)"} arrow>
            <WarningIcon sx={{ fontSize: 12, color: USAGE_COLORS[usageLevel] }} />
          </Tooltip>
        )}
      </Box>

      {/* Config chip */}
      <Box onClick={onClick} sx={{ display: { xs: "none", md: "block" } }}>
        {chatbot.has_config ? (
          <Chip icon={<ConfigIcon sx={{ fontSize: 9 }} />} label="Config" size="small"
            sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: "rgba(99,102,241,0.1)", color: "#6366f1", border: "1px solid rgba(99,102,241,0.2)", "& .MuiChip-icon": { color: "#6366f1", ml: 0.5 }, "& .MuiChip-label": { px: 0.5 } }} />
        ) : (
          <Chip label="Pendiente" size="small"
            sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: "rgba(245,158,11,0.1)", color: "#d97706", border: "1px solid rgba(245,158,11,0.2)", "& .MuiChip-label": { px: 0.75 } }} />
        )}
      </Box>

      {/* Toggle */}
      <Tooltip title={!chatbot.has_config ? "Configura primero" : chatbot.agent_enabled ? "Desactivar" : "Activar"} arrow>
        <span>
          <Switch size="small" checked={chatbot.agent_enabled} disabled={!chatbot.has_config}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            sx={{ width: 36, height: 20, p: 0,
              "& .MuiSwitch-switchBase": { p: "2px", "&.Mui-checked": { transform: "translateX(16px)", color: "#fff", "& + .MuiSwitch-track": { bgcolor: "#10b981", opacity: 1 } }, "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.3 } },
              "& .MuiSwitch-thumb": { width: 16, height: 16 },
              "& .MuiSwitch-track": { borderRadius: 10, bgcolor: "#cbd5e1", opacity: 1 },
            }} />
        </span>
      </Tooltip>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 0.25, opacity: hovered ? 1 : 0, transition: "opacity 0.2s" }}>
        {/* F4: Favorito */}
        <Tooltip title={isFavorite ? "Quitar favorito" : "Favorito"} arrow>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
            sx={{ p: 0.4, color: isFavorite ? "#f59e0b" : "#94a3b8",
              opacity: isFavorite ? 1 : undefined,
              "&:hover": { color: "#f59e0b", bgcolor: "rgba(245,158,11,0.1)" } }}>
            {isFavorite ? <StarIcon sx={{ fontSize: 14 }} /> : <StarBorderIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
        {/* F5: Exportar */}
        <Tooltip title="Exportar config" arrow>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onExport(); }}
            sx={{ p: 0.4, color: "#94a3b8", "&:hover": { color: "#0ea5e9", bgcolor: "rgba(14,165,233,0.1)" } }}>
            <ExportIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Duplicar" arrow>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            sx={{ p: 0.4, color: "#94a3b8", "&:hover": { color: "#6366f1", bgcolor: "rgba(99,102,241,0.1)" } }}>
            <CopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar" arrow>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }}
            sx={{ p: 0.4, color: "#94a3b8", "&:hover": { color: "#ef4444", bgcolor: "rgba(239,68,68,0.1)" } }}>
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <ArrowIcon onClick={onClick} sx={{ fontSize: 14, color: hovered ? "#a855f7" : "#cbd5e1", transition: "all 0.2s", transform: hovered ? "translateX(2px)" : "none" }} />
    </Box>
  );
}
